import { useMemo, memo } from "react";
import moment from "moment";
import styled from "@emotion/styled";

const Status = styled.span<{ isOnline: boolean }>`
    display: inline-block;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    margin-right: 5px;
    background-color: ${({ isOnline }) => (isOnline ? "#219653" : "#F2495E")};
`;

const Wrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

const Header = styled.div`
    display: flex;
    gap: 10px;
    font-size: 14px;

    .sub-title {
        color: #828fab;
        white-space: nowrap;
    }

    .online-status {
        margin-left: auto;
        white-space: nowrap;
    }
`;

const Bars = styled.div<{ $barNum: number }>`
    display: grid;
    grid-template-columns: repeat(${({ $barNum }) => $barNum}, 1fr);
    border-radius: 4px;
    gap: 2px;
    overflow: hidden;

    > div {
        height: 44px;
    }
`;

const Footer = styled.div`
    display: flex;
    justify-content: space-between;
    font-size: 10px;
    color: #828fab;
`;

const UptimeBars = ({
    onlineTimes,
    title,
    subTitle,
    barNum,
    isOnline,
}: {
    onlineTimes: {
        groupByDay: Record<number, number>;
        totalMinutes: number;
    } | null;
    title: string;
    subTitle: string;
    barNum: number;
    isOnline: boolean;
}) => {
    const statusBars = useMemo(() => {
        const colorRange = ["#69BF8E", "#99B86C", "#BB7946", "#CE7580"];
        let upTimeMinutes = 0;
        const totalMinutes = barNum * 24 * 60;

        const startDay = moment()
            .subtract(barNum - 1, "days")
            .startOf("day");

        const bars = Array.from({ length: barNum }, (_, i) => {
            const day = startDay.clone().add(i, "days").unix();
            const onlineMinutes = onlineTimes?.groupByDay?.[day] ?? 0;
            upTimeMinutes += onlineMinutes;
            const onlineHours = onlineMinutes / 60;

            const hoursPerColor = 24 / colorRange.length;
            let colorIndex = Math.min(
                Math.floor((24 - onlineHours) / hoursPerColor),
                colorRange.length - 1
            );

            // In case of any calculation issue that results in a negative index, we set it to 0 to avoid accessing out of bounds.
            // For example, sometime the API may return onlineMinutes slightly greater than 1440 (more than 24h)
            if (colorIndex < 0) {
                colorIndex = 0;
            }

            const color = colorRange[colorIndex];

            return {
                color,
                key: day,
            };
        });

        return {
            bars,
            upTimeMinutes,
            uptimePercent: ((upTimeMinutes / totalMinutes) * 100).toFixed(2),
        };
    }, [onlineTimes, barNum]);

    return (
        <Wrapper>
            <Header>
                <div className="text-truncate">{title}</div>
                <div className="sub-title">{subTitle}</div>
                <div className="online-status">
                    <Status isOnline={isOnline} />{" "}
                    {isOnline ? "Online" : "Offline"}
                </div>
            </Header>
            <Bars $barNum={barNum}>
                {statusBars.bars.map((bar) => (
                    <div
                        key={bar.key}
                        style={{ backgroundColor: bar.color }}
                    ></div>
                ))}
            </Bars>
            <Footer>
                <div>{barNum} days ago</div>
                <div>{statusBars.uptimePercent}% uptime</div>
                <div>Today</div>
            </Footer>
        </Wrapper>
    );
};

export default memo(UptimeBars);
